/**
 * src/pages/Prospeccion.jsx
 * Prospección del universo de proveedores del CM: avance de la extracción
 * (F2 seller_codes → F3 actividad 90d → F4 scoring), último run y leads.
 *
 * Permite seguir el avance desde el navegador sin comandos: los contadores por
 * estado se refrescan solos mientras corre el job.
 */
import { useState } from 'react'
import { Header } from '../components/layout/Header'
import { Badge } from '../components/ui/Badge'
import { Button } from '../components/ui/Button'
import { Card, KpiCard } from '../components/ui/Card'
import { Table } from '../components/ui/Table'
import { descargarProspeccionCsv, descargarProspeccionXlsx } from '../api/prospeccion'
import {
  useEjecutarProspeccion, useProspeccionLeads, useProspeccionStatus,
} from '../hooks/useProspeccion'

// Estados del ciclo de extracción, en orden de avance.
const ESTADOS = [
  { key: 'pendiente',            label: 'Pendiente',      color: 'gray',
    ayuda: 'Importado de F1; falta resolver su seller_code' },
  { key: 'seller_ok',            label: 'Seller OK',      color: 'blue',
    ayuda: 'Con seller_code; barriendo su actividad de 90 días' },
  { key: 'actividad_ok',         label: 'Actividad OK',   color: 'green',
    ayuda: 'Ventana de 90 días cubierta' },
  { key: 'seller_no_encontrado', label: 'Sin seller',     color: 'yellow',
    ayuda: 'BuscarProveedor no devolvió resultado; no se reintenta' },
  { key: 'error',                label: 'Error',          color: 'red',
    ayuda: 'Fallo persistente en la extracción' },
]

// Score A/B/C (PASO 4 del spec): A = target primario (intermedios).
const SCORE_COLOR = { A: 'green', B: 'blue', C: 'gray' }
const POSICION_COLOR = { 'Líder': 'purple', 'Medio': 'green', 'Cola': 'gray' }
const TENDENCIA_COLOR = { '↑': 'green', '→': 'gray', '↓': 'red' }

// Por qué se detuvo la corrida. Distinguirlo importa: 'throttling' no es un
// fallo — es prospección cediendo la cuota a los diagnósticos F1, que tienen
// prioridad — pero sí explica que se avance poco esa noche.
const MOTIVO = {
  completado:     { label: 'Sin trabajo pendiente', color: 'green' },
  cuota_agotada:  { label: 'Presupuesto agotado',   color: 'blue' },
  throttling:     { label: 'Cuota MP saturada',     color: 'yellow' },
  sin_ticket:     { label: 'Falta MP_API_TICKET',   color: 'red' },
}

function fmtFecha(iso) {
  if (!iso) return '—'
  return new Date(iso).toLocaleString('es-CL', {
    day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit',
  })
}

function fmtMonto(v) {
  if (v == null) return '—'
  return `$${Number(v).toLocaleString('es-CL')}`
}

const COLUMNS = [
  {
    key: 'razon_social', label: 'Proveedor',
    render: (v, row) => (
      <div>
        <div style={{ fontWeight: 500 }}>{v}</div>
        <div style={{ fontSize: '11px', color: 'var(--color-text-3)', fontFamily: 'var(--font-mono)' }}>
          {row.rut}{row.seller_code ? ` · ${row.seller_code}` : ''}
        </div>
      </div>
    ),
  },
  {
    key: 'score', label: 'Score', align: 'center', width: '70px',
    render: (v) => (v ? <Badge color={SCORE_COLOR[v] || 'gray'}>{v}</Badge> : '—'),
  },
  {
    key: 'posicion', label: 'Posición', width: '90px',
    render: (v) => (v ? <Badge color={POSICION_COLOR[v] || 'gray'}>{v}</Badge> : '—'),
  },
  {
    key: 'tendencia', label: 'Tend.', align: 'center', width: '70px',
    render: (v) => (v ? <Badge color={TENDENCIA_COLOR[v] || 'gray'}>{v}</Badge> : '—'),
  },
  { key: 'n_ocs_90d', label: 'OCs 90d', align: 'right', mono: true, width: '80px' },
  {
    key: 'monto_90d', label: 'Monto 90d', align: 'right', mono: true, nowrap: true,
    render: (v) => fmtMonto(v),
  },
  { key: 'n_organismos', label: 'Organismos', align: 'right', mono: true, width: '100px' },
  {
    key: 'contacto_persona', label: 'Contacto',
    render: (v, row) => (
      <div>
        <div>{v || <span style={{ color: 'var(--color-text-3)' }}>—</span>}</div>
        <div style={{ fontSize: '11px', color: 'var(--color-text-3)' }}>
          {row.email || row.telefono || ''}
        </div>
      </div>
    ),
  },
  {
    key: 'estado_contacto', label: 'CRM', width: '120px',
    render: (v) => <Badge color={v === 'Sin contactar' ? 'gray' : 'blue'}>{v || '—'}</Badge>,
  },
  {
    key: 'plan_sugerido', label: 'Plan', width: '90px',
    render: (v) => (v ? <Badge color={v === 'Premium' ? 'purple' : v === 'Pro' ? 'blue' : 'gray'}>{v}</Badge> : '—'),
  },
  {
    key: 'estado_extraccion', label: 'Extracción', width: '130px',
    render: (v) => {
      const e = ESTADOS.find(x => x.key === v)
      return <Badge color={e?.color || 'gray'} dot>{e?.label || v}</Badge>
    },
  },
  {
    key: 'ultimo_dia_procesado', label: 'Checkpoint', mono: true, nowrap: true, width: '110px',
    render: (v) => (v || '—'),
  },
]

export default function Prospeccion() {
  const [estado, setEstado] = useState('')
  const [descargando, setDescargando] = useState(false)
  const [aviso, setAviso] = useState(null)

  const { data: status, isLoading: cargandoStatus } = useProspeccionStatus()
  const { data: leads, isLoading: cargandoLeads } = useProspeccionLeads({ estado, limit: 100 })
  const ejecutar = useEjecutarProspeccion()

  const porEstado = status?.por_estado || {}
  const ultimo = status?.ultimo_run

  async function handleExport(formato) {
    setDescargando(formato)
    setAviso(null)
    try {
      await (formato === 'xlsx' ? descargarProspeccionXlsx() : descargarProspeccionCsv())
    } catch {
      setAviso({ tipo: 'error', texto: `No se pudo descargar el ${formato.toUpperCase()}.` })
    } finally {
      setDescargando(false)
    }
  }

  function handleEjecutar() {
    setAviso(null)
    ejecutar.mutate({ maxRequests: 8000, background: true }, {
      onSuccess: (r) => setAviso(r?.lanzado
        ? { tipo: 'ok', texto: 'Run lanzado en segundo plano. El avance se refresca solo.' }
        : { tipo: 'warn', texto: 'Ya hay una corrida en curso.' }),
      onError: () => setAviso({ tipo: 'error', texto: 'No se pudo lanzar el run.' }),
    })
  }

  return (
    <>
      <Header
        title="Prospección"
        subtitle="Universo de proveedores del Convenio Marco · job diario 03:00 (Chile)"
        action={
          <div style={{ display: 'flex', gap: '8px' }}>
            <Button variant="secondary" size="sm" onClick={() => handleExport('xlsx')}
                    loading={descargando === 'xlsx'}>
              Excel (4 hojas)
            </Button>
            <Button variant="ghost" size="sm" onClick={() => handleExport('csv')}
                    loading={descargando === 'csv'}>
              CSV
            </Button>
            <Button size="sm" onClick={handleEjecutar} loading={ejecutar.isPending}>
              Ejecutar ahora
            </Button>
          </div>
        }
      />

      <div style={{ padding: '20px 28px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
        {aviso && (
          <div style={{
            padding: '10px 14px', borderRadius: '8px', fontSize: '12px',
            fontFamily: 'var(--font-mono)',
            background: aviso.tipo === 'error' ? 'rgba(239,68,68,0.1)'
              : aviso.tipo === 'warn' ? 'rgba(245,158,11,0.1)' : 'rgba(16,185,129,0.1)',
            color: aviso.tipo === 'error' ? 'var(--color-error)'
              : aviso.tipo === 'warn' ? '#f59e0b' : '#10b981',
            border: '1px solid var(--color-border)',
          }}>
            {aviso.texto}
          </div>
        )}

        {/* Avance global */}
        <div style={{
          display: 'grid', gap: '12px',
          gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
        }}>
          <KpiCard label="TOTAL LEADS" value={cargandoStatus ? '…' : (status?.total_leads ?? 0)} />
          {ESTADOS.map(e => (
            <KpiCard
              key={e.key}
              label={e.label.toUpperCase()}
              value={cargandoStatus ? '…' : (porEstado[e.key] ?? 0)}
              sub={e.ayuda}
              color={
                e.color === 'green' ? '#10b981'
                : e.color === 'blue' ? '#3b82f6'
                : e.color === 'yellow' ? '#f59e0b'
                : e.color === 'red' ? '#ef4444' : 'var(--color-text-2)'
              }
            />
          ))}
        </div>

        {/* Último run */}
        <Card title="Último run" subtitle="Resumen de la última corrida (F2 → F3 → F4)">
          {ultimo ? (
            <div style={{
              display: 'grid', gap: '18px',
              gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
              fontSize: '13px',
            }}>
              <div>
                <div style={{ fontSize: '11px', color: 'var(--color-text-3)', fontFamily: 'var(--font-mono)' }}>FECHA</div>
                <div style={{ marginTop: '4px' }}>{fmtFecha(ultimo.fecha)}</div>
              </div>
              <div>
                <div style={{ fontSize: '11px', color: 'var(--color-text-3)', fontFamily: 'var(--font-mono)' }}>REQUESTS USADOS</div>
                <div style={{ marginTop: '4px', fontFamily: 'var(--font-mono)' }}>{ultimo.requests_usados}</div>
              </div>
              <div>
                <div style={{ fontSize: '11px', color: 'var(--color-text-3)', fontFamily: 'var(--font-mono)' }}>COMPLETADOS</div>
                <div style={{ marginTop: '4px', fontFamily: 'var(--font-mono)' }}>{ultimo.proveedores_completados}</div>
              </div>
              <div>
                <div style={{ fontSize: '11px', color: 'var(--color-text-3)', fontFamily: 'var(--font-mono)' }}>ERRORES</div>
                <div style={{
                  marginTop: '4px', fontFamily: 'var(--font-mono)',
                  color: ultimo.errores > 0 ? 'var(--color-error)' : 'var(--color-text)',
                }}>
                  {ultimo.errores}
                </div>
              </div>
              <div>
                <div style={{ fontSize: '11px', color: 'var(--color-text-3)', fontFamily: 'var(--font-mono)' }}>POR QUÉ TERMINÓ</div>
                <div style={{ marginTop: '4px' }}>
                  <Badge color={MOTIVO[ultimo.detalle?.f3?.motivo]?.color || 'gray'}>
                    {MOTIVO[ultimo.detalle?.f3?.motivo]?.label || ultimo.detalle?.f3?.motivo || '—'}
                  </Badge>
                </div>
              </div>
            </div>
          ) : (
            <div style={{ fontSize: '12px', color: 'var(--color-text-3)', fontFamily: 'var(--font-mono)' }}>
              Todavía no hay corridas registradas.
            </div>
          )}
        </Card>

        {/* Leads */}
        <Card
          title="Leads"
          subtitle="Score A (target primario) primero, y dentro de cada score por monto"
          padding="0"
          action={
            <select
              value={estado}
              onChange={e => setEstado(e.target.value)}
              style={{
                background: 'var(--color-surface-2)', color: 'var(--color-text)',
                border: '1px solid var(--color-border)', borderRadius: '6px',
                padding: '5px 10px', fontSize: '12px', fontFamily: 'var(--font-mono)',
              }}
            >
              <option value="">Todos los estados</option>
              {ESTADOS.map(e => (
                <option key={e.key} value={e.key}>{e.label}</option>
              ))}
            </select>
          }
        >
          <Table
            columns={COLUMNS}
            data={leads}
            loading={cargandoLeads}
            emptyMessage="Sin leads. Importa el JSON de F1 para poblar el universo."
          />
        </Card>
      </div>
    </>
  )
}
